import { ControllerHelper, MediaDownloadDto, PlaylistContentUploadDto, PlaylistController, PlaylistDownloadDto, PlayListNodeDownloadDto, PlaylistStateDownloadDto, PlaylistStateEnum, PostController, PostDownloadDto } from 'collaboration-service';
import CancelWithConfirmation from 'components/CancelWithConfirmation';
import SafeHTML from 'components/SafeHTML/SafeHTML';
import { formatDistanceToNow, formatRelative } from 'date-fns';
import { Button, Icon, Image, RatingView, useThemePart } from 'imaginarity-react-ui';
import * as _ from "lodash";
import * as React from 'react';
import { ApplicationState, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { convertMinutesToDaysHoursMinutes, getHigherDate, getHighestAbsDueDate, getHighestDueDateRelativeInHours, initPostDownloadDto, shallowCompare, visit } from 'services/Helpers';
import { may, mayDelete, mayUpdate } from 'services/Helpers/LinkHelpers';
import { getMediaLink } from 'services/Helpers/MediaHelpers';
import { createNamedRoute, gotoNamedRoute, linkToRoute, useCheckWhereIAm } from 'services/Helpers/RoutingHelper';
import { sanitizedAllowedTagsMediumSafetyContent } from 'services/Helpers/SanitizedHelper';
import { getTranslated, getTranslatedStandardDate, getTranslatedUserEmail, getTranslatedUserName } from 'services/Helpers/TranslationHelpers';
import { ImgI18N, useImgI18N } from 'services/ImgI18N';
import PlaylistPreview from './PlaylistPreview';
import { PlaylistSC as T } from "./PlaylistSC";

const mapper = (state: ApplicationState) => ({ contentLanguage: state.contentLanguage, user: state.user });

export interface PlaylistProps {
    data: PlaylistDownloadDto | PlaylistStateDownloadDto;
    backgroundToggle: boolean;
    isMobile: boolean;
    open?: boolean;
    toggleOpen?: () => void;
    onDelete?: () => void;
    firstChild?: boolean;
}

export const loadPostForPlaylist = async (c: PlaylistContentUploadDto, playlistId?: string) => {
    if (c.referenceId && c.referenceType !== "PALMS")
        return playlistId ?
            await ControllerHelper.singleCall({ id: c.referenceId, playlistId }, PostController.GetSinglePostForPlaylist, true) :
            (c.referenceType === "FileEntry" ?
                await ControllerHelper.singleCall({ id: c.referenceId }, PostController.GetSinglePostFromCMS, true) :
                await ControllerHelper.singleCall({ id: c.referenceId }, PostController.GetSinglePost, true));
    else {
        const post = initPostDownloadDto("", "PALMS");
        post.id = c.referenceId ?? "";
        return post;
    }
};


const Playlist = (props: PlaylistProps) => {
    const { open, data, toggleOpen, onDelete, firstChild } = props;
    const { contentLanguage, user } = useAppSelector(mapper, shallowCompare);
    const { t } = useImgI18N("playlist");
    const isMobileView = props.isMobile;
    const [playlist, setPlaylist] = React.useState<PlaylistDownloadDto>();
    const [posts, setPosts] = React.useState<PostDownloadDto[]>();
    const [media, setMedia] = React.useState<MediaDownloadDto>();
    const [state, setState] = React.useState<PlaylistStateEnum>();
    const dataType: "Playlist" | "PlaylistStates" = data.hasOwnProperty("playlist") ? "PlaylistStates" : "Playlist";
    const pState = data as PlaylistStateDownloadDto;

    React.useEffect(() => {
        if (dataType === "PlaylistStates") {
            const d = data as PlaylistStateDownloadDto;
            setPlaylist(d.playlist);
            setState(d.state);
            setMedia(d.playlist.media);
            const tasks = _.map(_.filter(d.playlist.contents, co => !co.hidden), async c => loadPostForPlaylist(c, undefined));
            Promise.all(tasks).then(res => setPosts(_.compact(res)));

        } else {
            const d = data as PlaylistDownloadDto;
            setPlaylist(d);
            setMedia(d.media);
        }
    }, [data, dataType]);

    // console.log("posts", posts);


    const m = _.find(media?.links, l => l.ref === "self")?.uri ?? "";

    // if (open && !posts && playlist) {
    //     Promise.all(_.map(_.filter(playlist.contents, co => !co.hidden), async c => {
    //         return (await ControllerHelper.singleCall({ id: c.referenceId ?? "", playlistId: playlist.id }, PostController.GetSinglePostForPlaylist));
    //     })).then(p => setPosts(p));
    // }

    // @Sascha: Please check code
    if (open && !posts && playlist) {
        Promise.all(
            _.map(
                _.filter(playlist.contents, co => !co.hidden),
                async c => {
                    if (c.referenceType !== "PALMS") {
                        return await ControllerHelper.singleCall(
                            { id: c.referenceId ?? "", playlistId: playlist.id },
                            PostController.GetSinglePostForPlaylist, true
                        );
                    }
                    else {
                        const post = initPostDownloadDto("", "PALMS");
                        post.id = c.referenceId ?? "";
                        return post;
                    }
                }
            )
        ).then(p => setPosts(_.compact(p)));
    }
    // @Sascha: Please check code



    const getDateFnsLng = React.useMemo(() => ImgI18N.getInstance().languageLocal(), []);
    const assignPlaylist = React.useCallback((id: string) => () => {
        gotoNamedRoute('playlist_assign', { id });
    }, []);

    const copyPl = playlist ? createNamedRoute("playlist_edit", { id: playlist.id, copy: "copy" }) : undefined;
    const editPl = playlist ? createNamedRoute("playlist_edit", { id: playlist.id }) : undefined;
    const isGA = _.find(user?.groupRoles, gr => gr.groupId === "GLOBAL" && _.find(gr.roles, r => r === "Admin") !== undefined) !== undefined;

    const delIt = React.useMemo(() => (withEvents: boolean) => async () => {
        if (playlist) {
            await ControllerHelper.singleCall({ playlistId: playlist.id, withEvents }, PlaylistController.ResetPlaylistState);
        }
    }, [playlist]);

    const check = useCheckWhereIAm();
    const isPlaylistHome = check("playlist_manage") || check("playlist");
    const absNodeDueDate = getHighestAbsDueDate(pState);
    const absNodeDueDateRelativeInHours = getHighestDueDateRelativeInHours(pState);

    const dueToDate = React.useMemo(() => {
        return getHigherDate(absNodeDueDate, absNodeDueDateRelativeInHours);
    }, [absNodeDueDate, absNodeDueDateRelativeInHours]);

    const onCancel = React.useCallback((id?: string) => () => {
        if (id) {
            PlaylistController.DeletePlaylist(
                { id },
                () => {
                    if (onDelete) onDelete();
                    gotoNamedRoute("playlist_manage");
                },
                error => console.log(error)
            );
        }
    }, [onDelete]);

    const curNodesDurationSum = (pState.curNodes || []).reduce((acc, curNode) => acc + (curNode.durationInMinutes || 0), 0);
    const prevNodesDurationSum = (pState.prevNodes || []).reduce((acc, prevNode) => acc + (prevNode.durationInMinutes || 0), 0);
    const nextNodesDurationSum = (pState.nextNodes || []).reduce((acc, nextNode) => acc + (nextNode.durationInMinutes || 0), 0);
    const sum = curNodesDurationSum + prevNodesDurationSum + nextNodesDurationSum;
    const sumDurationAll = React.useMemo(() => convertMinutesToDaysHoursMinutes(sum, t), [sum, t]);

    const nodes: PlayListNodeDownloadDto[] = [];
    if (playlist)
        visit(playlist.startNode, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));

    const hasChangedByDifferentUser = playlist?.creator.id !== playlist?.changedBy.id;
    const hasChanged = playlist?.created !== playlist?.changed;
    const emptyAvatarImageUrl = useThemePart(t => t.emptyAvatarImageUrl);

    const avatarCreator = getMediaLink(playlist?.creator, u => u?.avatar) ?? emptyAvatarImageUrl;
    const avatarChanger = getMediaLink(playlist?.changedBy, u => u?.avatar) ?? emptyAvatarImageUrl;

    const userMayAssign = React.useMemo(() =>
        may(playlist, "assignPlaylist") || may(playlist, "unassignPlaylist"),
        [playlist]
    );

    return (
        <T.Container
            isFirstChild={firstChild ?? false}
            open={open ?? false}
            backgroundToggle={isPlaylistHome ? props.backgroundToggle : false}
            done={state === "Finished"}
            isMobileView={isMobileView}
            active={open ?? false}
            isPlaylistHome={isPlaylistHome}
        >
            <div className="IconIndicator" />
            <T.LogoContainer onClick={isPlaylistHome ? open ? undefined : toggleOpen : undefined}>
                {m.indexOf("notFound") < 0 && <Image src={m} />}
            </T.LogoContainer>
            <T.DescriptionContainer onClick={isPlaylistHome ? open ? undefined : toggleOpen : undefined}>
                {playlist?.hidden &&
                    <div style={{ position: "absolute", right: 50, bottom: 0, height: 40, lineHeight: "40px", fontSize: "2em", opacity: 0.3, pointerEvents: "none" }}>
                        {t("invisble playlist")}
                    </div>
                }
                <T.Title >
                    <div style={{ float: "left" }}>
                        {getTranslated(playlist?.headlines, contentLanguage)?.text}
                    </div>

                    {playlist && playlist.rating > 0 && playlist.forbidRatings !== true &&
                        <div style={{ float: "right", marginTop: -8, marginRight: 5 }}>
                            <RatingView maxStars={5} rating={playlist.rating} filledColor='@accent' filledOut />
                        </div>
                    }

                </T.Title>
                {open &&
                    <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 20, width: "100%", paddingRight: 10, lineHeight: "30px" }}>
                        <div>
                            {dueToDate &&
                                <>
                                    <Icon name="calendar" marginTop={3} style={{ float: "left", marginRight: 5 }} />
                                    <div style={{ float: "left" }}>{playlist && formatRelative(new Date(dueToDate), new Date(), { locale: getDateFnsLng })}</div>
                                    <Icon name='chevron right' size="14px" marginTop={2} style={{ float: "left", marginLeft: 10, marginRight: 5 }} />
                                    <div style={{ float: "left", marginRight: 20 }}>{playlist && formatDistanceToNow(new Date(dueToDate), { addSuffix: true, locale: getDateFnsLng })}</div>
                                </>
                            }
                        </div>


                    </div>
                }
                <T.Description closed={!open} hasChangedByDifferentUser={hasChangedByDifferentUser}>
                    {playlist &&
                        <div style={{ display: "grid", gridTemplateColumns: "max-content max-content max-content 1fr", width: "100%", fontSize: "0.8em", lineHeight: "17px", paddingBottom: 5, columnGap: 15, borderBottom: "1px solid #b5b4ba", marginBottom: 5, marginTop: 5 }}>
                            <div style={{ fontWeight: "bolder" }}>Creator</div>
                            <div>
                                <Image src={avatarCreator} floated='left' rounded shadowed width='15px' squared />
                                <span style={{ marginLeft: -5 }}>{getTranslatedUserName(playlist.creator)}</span></div>
                            <div><a href={`mailto: ${getTranslatedUserEmail(playlist.creator)}`} style={{ color: "#313639" }}><Icon name="mail" style={{ float: "left", marginRight: 5 }} size={14} marginTop={1} /> {getTranslatedUserEmail(playlist.creator)}</a></div>
                            <div style={{ textAlign: "right" }}>
                                {getTranslatedStandardDate(playlist.created)}
                                {hasChanged && !hasChangedByDifferentUser && <span style={{ marginLeft: 10 }}>changed: ({getTranslatedStandardDate(playlist.changed)})</span>}
                            </div>
                            {hasChangedByDifferentUser &&
                                <>
                                    <div style={{ fontWeight: "bolder" }}>Changed By</div>
                                    <div>
                                        <Image src={avatarChanger} floated='left' rounded shadowed width='15px' squared />
                                        <span style={{ marginLeft: -5 }}>{getTranslatedUserName(playlist.changedBy)}</span></div>
                                    <div><a href={`mailto: ${getTranslatedUserEmail(playlist.changedBy)}`} style={{ color: "#313639" }}><Icon name="mail" style={{ float: "left", marginRight: 5 }} size={14} marginTop={1} /> {getTranslatedUserEmail(playlist.changedBy)}</a></div>
                                    <div style={{ textAlign: "right" }}>{getTranslatedStandardDate(playlist.changed)}</div>
                                </>
                            }
                        </div>
                    }

                    <SafeHTML
                        allowedTags={open ? sanitizedAllowedTagsMediumSafetyContent : ["strong", "b"]}
                        html={getTranslated(playlist?.descriptions, contentLanguage)?.text}
                    />
                    {open &&
                        <T.EntriesContainer>
                            <T.EntriesContainerTitle>
                                <Icon name="playlist" size={22} marginTop={6} style={{ float: "left", marginRight: 10 }} />
                                {playlist?.sequencingMode === "AllAtOnce" ? t("playlist elements") : t("playlist elements subdivided into phases")}
                            </T.EntriesContainerTitle>
                            {playlist && <PlaylistPreview nodes={nodes} posts={posts} id={playlist.id} />}
                        </T.EntriesContainer>
                    }
                </T.Description>
            </T.DescriptionContainer>
            {state && playlist &&
                <>
                    {isMobileView && !open &&
                        <T.StatusContainerSmall>
                            {dueToDate &&
                                <T.DateContainer isMobileView={isMobileView}>
                                    {formatRelative(new Date(dueToDate), new Date(), { locale: getDateFnsLng })}
                                    <Icon name="calendar" size={18} marginTop={3} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                </T.DateContainer>
                            }

                            <T.StateContainer isMobileView={isMobileView}>
                                {state === "Finished" ?
                                    <>
                                        {t("playlist done")}
                                        <Icon name="check" color="@accentGreen" size={18} marginTop={3} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </> :
                                    <>
                                        {t("playlist not done")}
                                        <Icon name="times circle" color="@accentRed" size={18} marginTop={3} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </>
                                }
                            </T.StateContainer>
                        </T.StatusContainerSmall>
                    }
                    {!isMobileView && !open &&

                        <T.StatusContainer>
                            {dueToDate &&
                                <>
                                    <T.DateContainer isMobileView={isMobileView}>
                                        {formatRelative(new Date(dueToDate), new Date(), { locale: getDateFnsLng })}
                                        <Icon name="calendar" size={18} marginTop={1} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </T.DateContainer>
                                    <T.DateContainer isMobileView={isMobileView} style={{ gridRow: 3, fontSize: "0.9em", marginTop: -5 }}>
                                        {formatDistanceToNow(new Date(dueToDate), { addSuffix: true, locale: getDateFnsLng })}
                                        <Icon name="empty" size={18} marginTop={3} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </T.DateContainer>
                                </>
                            }

                            <T.AssignContainer isMobileView={isMobileView}>
                                {t("assigned on {{date}}", { date: getTranslatedStandardDate(pState.assignmentDate, "PP") })}
                                <Icon name="user" size={18} marginTop={1} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                            </T.AssignContainer>

                            <T.DurationAll isMobileView={isMobileView}>
                                {t("estimatet duration")}{sumDurationAll}
                                <Icon name="clock" size={18} marginTop={1} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                            </T.DurationAll>

                            <T.StateContainer isMobileView={isMobileView} style={{ gridRow: dueToDate ? 4 : 2 }}>
                                {state === "Finished" ?
                                    <>
                                        {t("playlist done")}
                                        <Icon name="check" color="@accentGreen" size={18} marginTop={1} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </> :
                                    <>
                                        {t("playlist not done")}
                                        <Icon name="times circle" color="@accentRed" size={18} marginTop={1} style={{ float: isMobileView ? "left" : "right", marginLeft: isMobileView ? 0 : 10, marginRight: isMobileView ? 10 : 0 }} />
                                    </>
                                }
                            </T.StateContainer>
                        </T.StatusContainer>

                    }
                </>
            }
            <div style={{ position: "absolute", top: 0, bottom: 0, right: -1, background: open ? "rgba(0,0,0,0.05)" : "rgba(0,0,0,0.02)", width: 40 }} />
            {open &&
                <div style={{ position: "absolute", top: 0, right: -1 }}>
                    <Button floated="right" kind={open ? "transparentButton" : "halfTransparentButton"} icon={open ? "chevron up" : "chevron down"} onClick={toggleOpen} tooltip={{ tooltipText: open ? t("hide playlist content") : t("show playlist content") }} />
                </div>
            }
            <div style={{ position: "absolute", bottom: (firstChild && !open) ? 1 : 0, right: -1 }}>
                <Button floated="right" kind={open ? "transparentButton" : "halfTransparentButton"} icon={open ? "chevron up" : "chevron down"} onClick={toggleOpen} tooltip={{ tooltipText: open ? t("hide playlist content") : t("show playlist content") }} />
            </div>
            {isGA && state &&
                <>
                    <div style={{ position: "absolute", top: open ? 40 : 0, right: -1 }}>
                        <CancelWithConfirmation
                            cancel={delIt(false)}
                            iconColor="@darkGrey"
                            kind="halfTransparentButton"
                            icon="undo"
                            cancelText={t("cancel")}
                            confirmText={t("yes, reset state")}
                            tooltipInitialButton={t("reset state")}
                            iconOnly
                            floated='right'
                        />
                    </div>
                    <div style={{ position: "absolute", top: open ? 80 : 40, right: -1 }}>
                        <CancelWithConfirmation
                            cancel={delIt(true)}
                            iconColor="@darkGrey"
                            kind="halfTransparentButton"
                            icon="resume"
                            cancelText={t("cancel")}
                            confirmText={t("yes, reset state with events")}
                            tooltipInitialButton={t("reset state with events")}
                            iconOnly
                            floated='right'
                        />
                    </div>
                </>
            }

            {!state && editPl &&
                <div style={{ position: "absolute", top: !isPlaylistHome ? 0 : open ? 40 : -1, right: -1 }}>
                    <Button floated="right"
                        kind={mayUpdate(playlist) ? "halfTransparentButton" : "cancelButton"}
                        disabled={!mayUpdate(playlist)}
                        icon="edit"
                        asHtmlLinkToRoute={linkToRoute(editPl)}
                        cursor={mayUpdate(playlist) ? "pointer" : "not-allowed"}
                        tooltip={{
                            tooltipText: mayUpdate(playlist) ? t("edit playlist") : t("you are not allowed to edit"),
                            background: mayUpdate(playlist) ? undefined : "@accentRed",
                            fontColor: mayUpdate(playlist) ? undefined : "@mainBackground"
                        }}
                    />
                </div>
            }
            {!state && copyPl &&
                <div style={{ position: "absolute", top: !isPlaylistHome ? 40 : open ? 80 : 39, right: -1 }}>
                    <Button
                        kind={mayUpdate(playlist) ? "halfTransparentButton" : "cancelButton"}
                        floated="right"
                        disabled={!mayUpdate(playlist)}
                        icon="copy"
                        asHtmlLinkToRoute={linkToRoute(copyPl)}
                        tooltip={{
                            tooltipText: mayUpdate(playlist) ? t("copy playlist") : t("you are not allowed to copy"),
                            background: mayUpdate(playlist) ? undefined : "@accentRed",
                            fontColor: mayUpdate(playlist) ? undefined : "@mainBackground"

                        }}
                    />
                </div>
            }
            {!state &&
                <div style={{ position: "absolute", top: !isPlaylistHome ? 80 : open ? 120 : 79, right: -1 }} >
                    {
                        (mayDelete(playlist)) ?
                            <CancelWithConfirmation
                                iconOnly
                                icon='delete'
                                kind='halfTransparentButton'
                                cancel={onCancel(playlist?.id)}
                                floated='right'
                                cancelText={t("cancel")}
                                confirmText={t("yes, delete playlist")}
                                tooltipInitialButton={t("delete playlist")}
                                namespace='playlist'
                            />
                            :
                            <Button floated="right"
                                kind="cancelButton"
                                disabled
                                icon="delete"
                                cursor={"not-allowed"}
                                tooltip={{
                                    tooltipText: t("you are not allowed to delete"),
                                    background: (mayDelete(playlist)) ? undefined : "@accentRed",
                                    fontColor: (mayDelete(playlist)) ? undefined : "@mainBackground"
                                }}
                            />
                    }
                </div>
            }
            {!state && playlist &&
                <div style={{ position: "absolute", top: !isPlaylistHome ? 120 : open ? 160 : 119, right: -1 }}>
                    <Button floated="right"
                        kind={userMayAssign ? "halfTransparentButton" : "cancelButton"}
                        disabled={!userMayAssign}
                        icon="users" onClick={assignPlaylist(playlist.id)}
                        tooltip={{
                            tooltipText: userMayAssign ? t("assign playlist") : t("you are not allowed to assign"),
                            background: userMayAssign ? undefined : "@accentRed",
                            fontColor: userMayAssign ? undefined : "@mainBackground"
                        }}
                        cursor={userMayAssign ? "pointer" : "not-allowed"}
                    />
                </div>
            }
            {state === "Finished" &&
                <T.IconDoneContainer>
                    <Icon name="check" size={40} color='@accentGreen' marginTop={0} />
                </T.IconDoneContainer>
            }
        </T.Container>
    );
}
export default Playlist;
